import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MsalInterceptor } from '@azure/msal-angular';
import { TranslateService } from '@ngx-translate/core';
import { throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { MsalSessionService } from '../auth/msal-session.service';
import { TokenStorageService } from '../auth/token-storage.service';

const SESSION_ENDED_CODE = 'SESSION_ENDED';
const WRITE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

function isWriteToOwnProtectedApi(req: HttpRequest<unknown>): boolean {
  return (
    WRITE_METHODS.has(req.method) &&
    req.url.startsWith(`${environment.apiBaseUrl}/api/`) &&
    !req.url.startsWith(`${environment.apiBaseUrl}/api/v1/auth/`)
  );
}

/**
 * MsalInterceptor tries to silently acquire an Azure token for every request matching its
 * protectedResourceMap; with zero MSAL accounts (an OTP-only session) that silent acquisition
 * always fails and MsalInterceptor reacts by redirecting the browser to Microsoft login —
 * for every API call, not just the auth endpoints. An active OTP session must bypass
 * MsalInterceptor entirely so its own bearer token (attached by otpAuthInterceptor) is used as-is.
 *
 * Without any session (signed out in another tab) MsalInterceptor never sends the request and
 * never errors, so a save would hang with its button disabled. Writes to the own API therefore end
 * right here with 401 `SESSION_ENDED` and a notice, keeping the form input. The backend uses the same
 * code for an ended session; there the OTP session still exists and authErrorInterceptor ends it.
 */
export const msalGateInterceptor: HttpInterceptorFn = (req, next) => {
  const tokenStorage = inject(TokenStorageService);
  if (tokenStorage.hasSession()) {
    return next(req);
  }

  // Method and URL first: TranslateService loads its files through this interceptor while being
  // constructed, so nothing beyond this point may be resolved for those requests.
  if (isWriteToOwnProtectedApi(req) && !inject(MsalSessionService).hasAccount()) {
    const injector = inject(Injector);
    setTimeout(() =>
      injector.get(MatSnackBar).open(injector.get(TranslateService).instant('fehler.sitzungBeendet'), undefined, {
        panelClass: 'snackbar-error',
      }),
    );
    return throwError(
      () =>
        new HttpErrorResponse({
          status: 401,
          statusText: 'Session ended',
          url: req.url,
          error: { code: SESSION_ENDED_CODE },
        }),
    );
  }

  const msalInterceptor = inject(MsalInterceptor);
  return msalInterceptor.intercept(req, { handle: next });
};
